/// <amd-module name="Dashboards/WidgetList/DashboardWidgetList" />
import React = require("react");

import { Widget } from "Dashboards/Common/Contracts/Dashboard";
import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import { WidgetCatalogInformation } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { WidgetPresentationMode, WidgetTeamContext } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";
import * as Resources from "Dashboards/WidgetList/Resources";
import { format, localeComparer } from "VSS/Core/Util/String";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { FocusZoneContext } from "VSSUI/FocusZone";
import { IListItemDetails, IListRow, List, ListItem, ListSelection } from "VSSUI/List";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { Callout } from "VSSUI/Callout";
import { ContentJustification, ContentLocation, ContentOrientation } from "VSSUI/Callout";

export interface DashboardWidgetListProps extends IVssComponentProperties {
    parentPageName: string;
    widgets: Widget[];
    selectedItem: Widget | undefined;
    widgetCatalog: WidgetCatalogInformation | undefined;
    teamContext: WidgetTeamContext;

    onSelect: (selectedItem: Widget) => void;
    onDoubleClick: (selectedItem: Widget) => void;
}

/** This panel is used to allow a user to choose a widget. */
export class DashboardWidgetList extends VssComponent<DashboardWidgetListProps> {
    public render(): JSX.Element | null {
        const entries = this.props.widgets.slice().sort((a, b) => {
            return localeComparer(a.name, b.name);
        });

        const useMultiSelect = false;
        const selection = new ListSelection(useMultiSelect);
        if (this.props.selectedItem !== undefined) {
            selection.select(
                entries.findIndex((item: Widget) => {
                    return this.props.selectedItem !== undefined && item.contributionId === this.props.selectedItem.contributionId;
                })
            );
        }
        const rowHeight = 100;
        const maxRows = window.innerHeight / rowHeight + 1; // We want offscreen content of the list virtualized, to avoid needless loads of widgets.
        return (
            <div>
                <List
                    key="widget-catalog-list"
                    className="widget-catalog-list"
                    ariaLabel={Resources.WidgetListLabel}
                    itemProvider={new ArrayItemProvider<Widget>(entries)}
                    pageSize={maxRows}
                    renderRow={this.renderRow}
                    width="100%"
                    selection={selection}
                    onSelect={this.onSelect}
                />
            </div>
        );
    }

    private renderRow = (index: number, widget: Widget, details: IListItemDetails<Widget>): JSX.Element => {
        const catalogEntry =
            this.props.widgetCatalog &&
            this.props.widgetCatalog.catalogEntries.find(item => {
                return item.contributionId == widget.contributionId;
            });
        return (
            <ListItem key={"list-item-" + widget.contributionId} className="widget-row" details={details} index={index}>
                <FocusZoneContext.Consumer>
                    {cellContext => (
                        <React.Fragment>
                            {
                                <WidgetListItem
                                    parentPageName={this.props.parentPageName}
                                    onDoubleClick={this.props.onDoubleClick}
                                    widget={widget}
                                    isSelected={this.props.selectedItem && widget.contributionId === this.props.selectedItem.contributionId}
                                    catalogEntry={catalogEntry}
                                    teamContext={this.props.widgetTeamContext}
                                />
                            }
                        </React.Fragment>
                    )}
                </FocusZoneContext.Consumer>
            </ListItem>
        );
    };

    private onSelect = (event: React.SyntheticEvent<HTMLElement>, listRow: IListRow<Widget>): void => {
        this.props.onSelect(listRow.data);
    };
}

export interface WidgetListItemProps {
    parentPageName: string;
    widget: Widget;
    catalogEntry: WidgetCatalogEntry | undefined;
    teamContext: WidgetTeamContext;

    isSelected: boolean | undefined;
    onDoubleClick: (selectedItem: Widget) => void;
}

export class WidgetListItem extends VssComponent<WidgetListItemProps> {
    public render(): JSX.Element | null {
        const widget = toWidgetConfigurationProps(this.props.widget);
        const adjustedSize = 98; // 98 pixel squares, allowing 2 px of gray borders.
        const widgetSize = WidgetSizeConverter.ConvertToPixelSize(this.props.widget.size);
        const scale = Math.min(adjustedSize / widgetSize.width, adjustedSize / widgetSize.height);

        return (
            <div className="widget-list-item flex flex-noshrink flex-row" onDoubleClick={this.onDoubleClick}>
                <div className="widget-host-sizer">
                    <div className="widget-host-zoom-box" style={{ transform: `scale(${scale})` }}>
                        <WidgetHost
                            widgetPresentationMode={WidgetPresentationMode.embeddedView}
                            parentPageName={this.props.parentPageName}
                            widgetProps={widget}
                            convertToPixelSize={WidgetSizeConverter.ConvertToPixelSize}
                            teamContext={this.props.teamContext}
                        />
                    </div>
                </div>
                <div className="widget-details">
                    <div className="widget-name-container">
                        <div className="widget-name fontSizeL text-ellipsis">{this.props.widget.name}</div>
                    </div>
                    {this.props.catalogEntry && <div className="widget-type  text-ellipsis">{this.props.catalogEntry.name}</div>}
                    <div className="widget-width">
                        Size: {format(Resources.SizeFormat, this.props.widget.size.columnSpan, this.props.widget.size.rowSpan)}
                    </div>
                </div>
                {this.props.isSelected && this.renderCallout()}
            </div>
        );
    }

    private renderCallout() {
        // This path is disabled right now. Callout needs some investigation to not be modal in page level, and to honor horizontal start-end, before it is appropriate for inclusion.
        const enableCallout = false;
        const widget = toWidgetConfigurationProps(this.props.widget);
        return (
            this.props.isSelected &&
            enableCallout && (
                <Callout
                    contentJustification={ContentJustification.Center}
                    contentLocation={ContentLocation.Center}
                    contentOrientation={ContentOrientation.Column}
                    modal={false}
                >
                    <WidgetHost
                        widgetPresentationMode={WidgetPresentationMode.embeddedView}
                        parentPageName={this.props.parentPageName}
                        widgetProps={widget}
                        convertToPixelSize={WidgetSizeConverter.ConvertToPixelSize}
                        teamContext={this.props.teamContext}
                    />
                </Callout>
            )
        );
    }

    private onDoubleClick = (event: React.MouseEvent<HTMLElement>): void => {
        this.props.onDoubleClick(this.props.widget);
    };
}

function toWidgetConfigurationProps(widget: Widget): DashboardWidgetProps {
    return {
        ...widget,
        contributionId: widget.contributionId
    };
}
